import { z } from 'zod';
import { logger } from '@/lib/logger';

// Valid product categories as per the system
export const VALID_CATEGORIES = [
  'camera',
  'speaker',
  'amplifier',
  'processor',
  'display',
  'touch_panel',
  'access_point',
  'switch',
  'sensor',
  'other',
] as const;

// Schema for product dimensions
const DimensionsSchema = z
  .object({
    width: z.number().nullable(),
    height: z.number().nullable(),
    depth: z.number().nullable(),
    unit: z.enum(['mm', 'inches']),
  })
  .nullable();

// Schema for weight
const WeightSchema = z
  .object({
    value: z.number().nullable(),
    unit: z.enum(['kg', 'lbs']),
  })
  .nullable();

// Schema for power specifications
const PowerSchema = z
  .object({
    consumption: z.number().nullable(),
    unit: z.string().default('W'),
    voltage: z.string().nullable(),
  })
  .nullable();

// Schema for I/O connections
const IOConnectionSchema = z.object({
  name: z.string(),
  type: z.enum(['audio', 'video', 'control', 'power', 'network']),
  connector: z.string(),
});

// Schema for basic product info
const BasicInfoSchema = z.object({
  brand: z.string().min(1, 'Brand is required'),
  model: z.string().min(1, 'Model is required'),
  partNumber: z.string().nullable(),
  category: z.string().min(1),
  subcategory: z.string().nullable(),
  description: z.string().nullable(),
});

// Schema for specifications
const SpecificationsSchema = z
  .object({
    dimensions: DimensionsSchema,
    weight: WeightSchema,
    power: PowerSchema,
    // Allow additional properties for category-specific specs
  })
  .passthrough();

// Schema for I/O
const IOSchema = z
  .object({
    inputs: z.array(IOConnectionSchema).default([]),
    outputs: z.array(IOConnectionSchema).default([]),
  })
  .nullable();

// Schema for pricing
const PricingSchema = z
  .object({
    msrp: z.number().nullable(),
    currency: z.string().default('USD'),
    asOf: z.string().nullable(),
  })
  .nullable();

// Main product search result schema
export const ProductSearchResultSchema = z.object({
  basic: BasicInfoSchema,
  specifications: SpecificationsSchema,
  io: IOSchema,
  pricing: PricingSchema,
  confidence: z.number().min(0).max(1),
  provider: z.string(),
});

export type ProductSearchResult = z.infer<typeof ProductSearchResultSchema>;

/**
 * Validates and transforms LLM response data
 * Applies post-processing heuristics to fix common issues
 */
export function validateAndTransformProduct(data: unknown): ProductSearchResult | null {
  try {
    // First, ensure data is an object
    if (typeof data !== 'object' || data === null) {
      return null;
    }

    const rawData = data as any;

    // Apply heuristics before validation
    const transformed = {
      ...rawData,
      basic: {
        ...rawData.basic,
        // Extract brand from model if missing
        brand: rawData.basic?.brand || extractBrandFromModel(rawData.basic?.model || ''),
        // Ensure model is present
        model: rawData.basic?.model || rawData.query || 'Unknown',
        // Map invalid categories
        category: mapToValidCategory(rawData.basic?.category),
      },
      // Ensure specifications exist with required nested objects
      specifications: {
        dimensions: rawData.specifications?.dimensions ?? null,
        weight: rawData.specifications?.weight ?? null,
        power: rawData.specifications?.power ?? null,
        ...rawData.specifications,
      },
      // Ensure io exists (can be null)
      io: rawData.io ?? null,
      // Ensure pricing exists (can be null)
      pricing: rawData.pricing ?? null,
      // Default confidence if missing
      confidence: rawData.confidence ?? 0.5,
      // Default provider
      provider: rawData.provider || 'unknown',
    };

    // Validate with Zod
    return ProductSearchResultSchema.parse(transformed);
  } catch (error) {
    logger.error('Product validation failed:', error);
    return null;
  }
}

/**
 * Extract brand from model string
 * e.g., "Sony SRG-300H" -> "Sony"
 */
function extractBrandFromModel(model: string): string {
  if (!model) return 'Unknown';

  const parts = model.trim().split(/\s+/);
  if (parts.length > 1) {
    // Common brand patterns
    const knownBrands = ['Sony', 'Savant', 'JBL', 'QSC', 'Cisco', 'Panasonic', 'Samsung', 'LG'];
    const firstWord = parts[0];

    // Check if first word matches known brand
    const matchedBrand = knownBrands.find(brand => firstWord.toLowerCase() === brand.toLowerCase());

    return matchedBrand || firstWord;
  }

  return 'Unknown';
}

/**
 * Map common category aliases to canonical names
 * Preserves custom/unknown categories as-is
 */
function mapToValidCategory(category: string | undefined): string {
  if (!category) return 'other';

  const categoryMap: Record<string, string> = {
    audio_processor: 'processor',
    audio_amplifier: 'amplifier',
    video_processor: 'processor',
    ptz_camera: 'camera',
    ip_camera: 'camera',
    touchpanel: 'touch_panel',
    'touch-panel': 'touch_panel',
    'access-point': 'access_point',
    network_switch: 'switch',
  };

  // Check if already a known category
  if (VALID_CATEGORIES.includes(category as any)) {
    return category;
  }

  // Try mapping common aliases
  const mapped = categoryMap[category.toLowerCase()];
  if (mapped) return mapped;

  // Preserve the original category (supports custom/user-defined categories)
  return category;
}
